/**
 * This mixin is used to add filtering logic to a list of items. Filtering
 * occurs differently depending of the type of the data, so the `types` mixin
 * is also used
 */

import types from './types.mixin.js';

export default {
  props: {
    startsWithFilterColumn: {
      type: Array,
      default() {
        return [];
      },
    },
    exactFilterColumn: {
      type: Array,
      default() {
        return [];
      },
    },
    items: {
      type: Array,
      default() {
        return [];
      },
    },
    filterServerSide: {
      type: Boolean,
    },
    serverSideTotalItems: {
      type: Number,
      default() {
        return 0;
      },
    },
    urlFilterKey: {
      type: String,
      default() {
        return undefined;
      },
    },
    urlFilterTypesKey: {
      type: String,
      default() {
        return undefined;
      },
    },
    customSortHeaders: {
      type: Object,
      default() {
        return {};
      },
    },
    skipFilterWithEscSpecialChar: {
      type: Boolean,
      default() {
        return false;
      },
    },
  },
  mixins: [types],
  data() {
    return {
      filters: {},
      filterTypes: {},
      filteredItems: [],
    };
  },
  mounted() {
    if (this.urlFilterTypesKey !== undefined)
      this.filterTypes = this.getObjectFromUrlQueryKey(this.urlFilterTypesKey);
    if (this.urlFilterKey !== undefined)
      this.filters = this.getObjectFromUrlQueryKey(this.urlFilterKey);
    this.filteredItems = this.items;
  },
  watch: {
    filters: {
      handler() {
        this.emitFilterUpdate();
        this.setFilteredItems();
      },
      deep: true,
    },
    filterTypes: {
      handler() {
        this.emitFilterUpdate();
        this.setFilteredItems();
      },
      deep: true,
    },
  },
  computed: {
    serverSideTotal() {
      if (this.filterServerSide) {
        return this.serverSideTotalItems;
      }
      return undefined;
    },
    filterObj() {
      // this object combines filters and filterTypes so it's easier to look
      // them up together by the column name
      let result = {};
      for (let property in this.items[0]) {
        result[property] = {
          value: this.filters[property],
          type: this.filterTypes[property],
        };
      }
      return result;
    },
    filterSql() {
      return Object.keys({ ...this.filters, ...this.filterTypes })
        .map((column) => {
          let value = this.filters[column];
          let colType = this.getColType(column);
          if (colType === undefined && typeof value !== 'undefined') {
            // determine the type
            // NOTE: This is required in the case that this.items
            // is undefined or has zero items in types.mixin.js
            if (typeof value === 'boolean') {
              colType = 'string';
            } else if (!isNaN(value)) {
              colType = 'number';
            } else if (this.isDate(value)) {
              colType = 'date';
            } else {
              colType = 'string';
            }
          }
          const columnType = colType;
          const filterType = this.filterTypes[column];
          return { column, value, columnType, filterType };
        })
        .filter(
          (f) =>
            (f.value !== undefined && f.value !== '') ||
            f.filterType === 'empty'
        )
        .map(({ column, value, columnType, filterType }) => {
          column = `[${column}]`;
          if (columnType === undefined) {
            columnType = 'string';
          }
          if (filterType === 'empty') {
            const notStr = columnType !== 'string';
            return notStr ? `${column} is null` : `isnull(${column}, '') = ''`;
          }
          let comparator = this.getComparator(filterType, columnType, column);
          if (columnType === 'string') {
            value = this.filteredValue(value, column);
          }
          if (columnType === 'date') {
            column = `cast(${column} as date)`;
            value = `'${value}'`;
          }
          return `${column} ${comparator} ${value}`;
        })
        .join(' AND ');
    },
  },
  methods: {
    emitFilterUpdate() {
      this.$emit('update:filters', {
        filters: this.filters,
        filterTypes: this.filterTypes,
        filterSql: this.filterSql,
        tableId: this.tableId,
      });
    },
    setFilteredItems() {
      if (typeof this.items === 'undefined' || typeof this.items !== 'object')
        this.filteredItems = [];
      if (this.filterServerSide) {
        this.filteredItems = this.items;
      } else {
        this.$emit('update-loader', true);
        let filteredItems = this.items.filter((item) => {
          for (let k in this.filterObj) {
            const filter = `${this.filterObj[k].value}`.toLowerCase();
            const value = `${item[k]}`.toLowerCase();
            const type = this.types[k] || 'string';
            const filterType = this.filterObj[k].type || 'equal';
            const columnName = k;
            // check each filter - only bail out if an item is filtered out
            // i.e. it must fulfill all filters
            if (
              this.isFilteredOut(value, type, filter, filterType, columnName)
            ) {
              return false;
            }
          }
          return true;
        });
        setTimeout(() => this.$emit('update-loader', false), 500);
        this.filteredItems = filteredItems;
      }
    },
    getColType(column) {
      if (this.types[column]) {
        return this.types[column];
      }
      if (this.forceTypes[column]) {
        return this.forceTypes[column];
      }
      return undefined;
    },
    filteredValue(filteredColValue, column) {
      if (!this.skipFilterWithEscSpecialChar) {
        var specialChar = ['\\', ';', '=', '$', '_', '[', ']', '%', '^'];
        for (var index in specialChar) {
          if (filteredColValue.includes(specialChar[index])) {
            var regex = new RegExp(this.escapeRegExp(specialChar[index]), 'g');
            filteredColValue = filteredColValue.replace(
              regex,
              `\\${specialChar[index]}`
            );
          }
        }
      }
      filteredColValue = filteredColValue.replace("'", "''");
      filteredColValue = this.getSqlFilteredColValue(filteredColValue, column);
      filteredColValue = this.skipFilterWithEscSpecialChar
        ? filteredColValue
        : filteredColValue + ` escape '\\'`;
      return filteredColValue;
    },
    getSqlFilteredColValue(filteredColValue, column) {
      if (
        this.startsWithFilterColumn.length > 0 &&
        this.startsWithFilterColumn.includes(column)
      ) {
        return `'${filteredColValue}%'`;
      } else if (
        this.exactFilterColumn.length > 0 &&
        this.exactFilterColumn.includes(column)
      ) {
        return `'${filteredColValue}'`;
      } else {
        return `'%${filteredColValue}%'`;
      }
    },
    getComparator(filterType, columnType, column) {
      let comparator = columnType === 'string' ? 'like' : '=';
      switch (filterType) {
        case 'greater':
          comparator = '>';
          break;
        case 'less':
          comparator = '<';
          break;
        default:
          comparator =
            columnType === 'string'
              ? this.exactFilterColumn.length > 0 &&
                this.exactFilterColumn.includes(column)
                ? '='
                : 'like'
              : '=';
          break;
      }
      return comparator;
    },
    isFilteredOut(value, type, filter, filterType, columnName) {
      // filtering for empty values - just return if the value is populated
      if (filterType === 'empty') {
        return this.isPopulatedValue(value);
      }
      // if there's no filter then the column can't be filtered out
      if (!this.isPopulatedValue(filter)) {
        return false;
      }
      // if there's no value and we're not filtering for empty values then the row
      // is filtered out
      if (!this.isPopulatedValue(value)) {
        return true;
      }
      // if nothing above catches it then we have to check based on the type
      return (
        this[`${type}IsFilteredOut`](value, filter, filterType, columnName) ||
        false
      );
    },
    stringIsFilteredOut(value, filter, filterType, columnName) {
      if (
        this.startsWithFilterColumn.length > 0 &&
        this.startsWithFilterColumn.includes(columnName)
      )
        return !value.startsWith(filter);
      else return !value.includes(filter);
    },
    numberIsFilteredOut(value, filter, filterType) {
      // if there is no comparator we do direct value comparison
      const isFiltered = {
        equal: +value !== +filter,
        less: +value >= +filter,
        greater: +value <= +filter,
      };
      return isFiltered[filterType];
    },
    escapeRegExp(str) {
      //escape the special characters with their escape codes
      /* eslint-disable-next-line no-useless-escape */
      return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
    },
    dateIsFilteredOut(value, filter, filterType) {
      //1970-01-01T00:00:00 -> 1970-01-01
      const parsedValue = value.toLowerCase().split('t')[0];
      let [vYear, vMonth, vDay] = parsedValue.split('-');

      // parse out date pieces and comparator
      let [fYear, fMonth, fDay] = filter.split('-');

      // combine dates into numbers for easier comparison
      const valueToCompare = `${fYear ? vYear : '0000'}${
        fMonth ? vMonth : '00'
      }${fDay ? vDay : '00'}`;
      const filterToCompare = `${fYear || '0000'}${fMonth || '00'}${
        fDay || '00'
      }`;
      const isFiltered = {
        equal: valueToCompare !== filterToCompare,
        less: valueToCompare >= filterToCompare,
        greater: valueToCompare <= filterToCompare,
      };
      return isFiltered[filterType];
    },
    getColumnType(column) {
      let type = this.getType(column);
      // replace 'string' with 'text' so this can be used for the input type
      return type === 'string' ? 'text' : type;
    },
    updateFilter(column, filterValue) {
      if (!['less', 'greater', 'empty', 'equal'].includes(filterValue)) {
        throw `filterable.mixin: unrecognized filterValue '${filterValue}'`;
      }
      this.$set(this.filterTypes, column, filterValue);
    },
    getObjectFromUrlQueryKey(key) {
      if (key === undefined) return {};
      const objectString = this.$route.query[key];
      if (objectString === undefined) return {};
      return JSON.parse(decodeURIComponent(objectString));
    },
  },
};
